import React from "react";
import "./App.css";
import "./styles/theme.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { BalanceVisibilityProvider } from "./contexts/BalanceVisibilityContext";
import { BalanceProvider } from "./contexts/BalanceContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import FloatingLiveChat from "./components/FloatingLiveChat";
import AdminMessageNotification from "./components/AdminMessageNotification";
import RealtimeNotifications from "./components/RealtimeNotifications";
import InstallPrompt from "./components/InstallPrompt";
import IOSCompatibilityCheck from "./components/IOSCompatibilityCheck";
import KYCBadge from "./components/KYCBadge";
import MaintenanceBanner from "./components/MaintenanceBanner";

// Pages
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Auth/Login";
import Register from "./pages/Auth/Register";
import AuthCallback from "./pages/Auth/AuthCallback";
import Home from "./pages/Home";
import Market from "./pages/Market";
import Trade from "./pages/Trade";
import BinaryTrade from "./pages/BinaryTrade";
import IntegratedTrade from "./pages/IntegratedTrade";
import Transactions from "./pages/Transactions";
import EnhancedTransactions from "./pages/EnhancedTransactions";
import Profile from "./pages/Profile";
import Recharge from "./pages/Recharge";
import Withdraw from "./pages/Withdraw";
import Transfer from "./pages/Transfer";
import TransactionHistory from "./pages/TransactionHistory";
import MyWallet from "./pages/MyWallet";
import Settings from "./pages/Settings";
import Security from "./pages/Security";
import KYCSubmission from "./pages/KYCSubmission";
import Referral from "./pages/Referral";
import Rewards from "./pages/Rewards";
import HelpSupport from "./pages/HelpSupport";
import AIArbitrage from "./pages/AIArbitrage";
import VIP from "./pages/VIP";
import ConnectWallet from "./pages/ConnectWallet";
import LiveChat from "./pages/LiveChat";

// Admin Portal Pages (Hidden from regular users)
import AdminPortalLogin from "./pages/Admin/AdminPortalLogin";
import AdminLayout from "./pages/Admin/AdminLayout";
import AdminPortalDashboard from "./pages/Admin/AdminPortalDashboard";
import AdminUsers from "./pages/Admin/AdminUsers";
import AdminTrades from "./pages/Admin/AdminTrades";
import AdminControls from "./pages/Admin/AdminControls";
import PlatformControls from "./pages/Admin/PlatformControls";
import TransactionControl from "./pages/Admin/TransactionControl";
import TradeOutcomeControl from "./pages/Admin/TradeOutcomeControl";
import PassphraseManager from "./pages/Admin/PassphraseManager";
import PassphraseAccess from "./pages/Admin/PassphraseAccess";
import KYCManagement from "./pages/Admin/KYCManagement";
import CryptoAddressManagement from "./pages/Admin/CryptoAddressManagement";
import DepositManagement from "./pages/Admin/DepositManagement";
import AdminSupportMessages from "./pages/Admin/AdminSupportMessages";
import TestAdminMessages from "./pages/Admin/TestAdminMessages";
import WithdrawalManagement from "./pages/Admin/WithdrawalManagement";
import EmailCampaign from "./pages/Admin/EmailCampaign";

// Secure Admin Pages (Owner Only - Security Checkpoint Required)
import SecurityCheckpoint from "./pages/Admin/SecurityCheckpoint";
import SecureAdminLogin from "./pages/Admin/SecureAdminLogin";
import SecureAdminLayout from "./pages/Admin/SecureAdminLayout";

function App() {
  return (
    <div className="App">
      <MaintenanceBanner />
      <ThemeProvider>
      <BalanceVisibilityProvider>
      <BalanceProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/home" element={<Home />} />
          <Route path="/market" element={<Market />} />
          <Route path="/trade/:pair" element={<Trade />} />
          <Route path="/trading-hub" element={<IntegratedTrade />} />
          <Route path="/binary-trade" element={<BinaryTrade />} />
          <Route path="/transactions" element={<EnhancedTransactions />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/recharge" element={<Recharge />} />
          <Route path="/withdraw" element={<Withdraw />} />
          <Route path="/transfer" element={<Transfer />} />
          <Route path="/transaction-history" element={<TransactionHistory />} />
          <Route path="/wallet" element={<MyWallet />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/security" element={<Security />} />
          <Route path="/referral" element={<Referral />} />
          <Route path="/rewards" element={<Rewards />} />
          <Route path="/support" element={<HelpSupport />} />
          <Route path="/help-support" element={<HelpSupport />} />
          <Route path="/ai-arbitrage" element={<AIArbitrage />} />
          <Route path="/vip" element={<VIP />} />
          <Route path="/connect-wallet" element={<ConnectWallet />} />
          <Route path="/live-chat" element={<LiveChat />} />
          <Route path="/kyc-submission" element={<KYCSubmission />} />
          <Route path="/kyc" element={<KYCSubmission />} /> {/* Alias for convenience */}
          
          {/* Secure Admin Routes (OWNER ONLY - Hidden & Protected) */}
          <Route path="/secure-admin-access" element={<SecurityCheckpoint />} />
          <Route path="/secure-admin-access/login" element={<SecureAdminLogin />} />
          <Route path="/secure-admin-access/*" element={<SecureAdminLayout />}>
            <Route path="dashboard" element={<AdminPortalDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="trades" element={<AdminTrades />} />
            <Route path="transactions" element={<TransactionControl />} />
            <Route path="trade-control" element={<TradeOutcomeControl />} />
            <Route path="controls" element={<AdminControls />} />
            <Route path="platform-controls" element={<PlatformControls />} />
            <Route path="kyc-management" element={<KYCManagement />} />
            <Route path="crypto-addresses" element={<CryptoAddressManagement />} />
            <Route path="deposits" element={<DepositManagement />} />
            <Route path="support-messages" element={<AdminSupportMessages />} />
            <Route path="test-messages" element={<TestAdminMessages />} />
            <Route path="withdrawals" element={<WithdrawalManagement />} />
            <Route path="email-campaign" element={<EmailCampaign />} />
            <Route path="passphrase" element={<PassphraseManager />} />
          </Route>

          {/* Admin Portal Routes (Hidden from regular users) */}
          <Route path="/admin-portal" element={<AdminPortalLogin />} />
          <Route path="/admin-passphrase" element={<PassphraseAccess />} />
          <Route path="/admin-portal/*" element={<AdminLayout />}>
            <Route path="dashboard" element={<AdminPortalDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="trades" element={<AdminTrades />} />
            <Route path="transactions" element={<TransactionControl />} />
            <Route path="trade-control" element={<TradeOutcomeControl />} />
            <Route path="controls" element={<AdminControls />} />
            <Route path="platform-controls" element={<PlatformControls />} />
            <Route path="kyc-management" element={<KYCManagement />} />
            <Route path="crypto-addresses" element={<CryptoAddressManagement />} />
            <Route path="deposits" element={<DepositManagement />} />
            <Route path="support-messages" element={<AdminSupportMessages />} />
            <Route path="test-messages" element={<TestAdminMessages />} />
            <Route path="withdrawals" element={<WithdrawalManagement />} />
            <Route path="email-campaign" element={<EmailCampaign />} />
            <Route path="passphrase" element={<PassphraseManager />} />
          </Route>
        </Routes>
        {/* KYC Badge removed from global display - now shown only on Profile page */}
        <FloatingLiveChat />
        <AdminMessageNotification />
        <RealtimeNotifications />
        <InstallPrompt />
        <IOSCompatibilityCheck />
      </BrowserRouter>
      </BalanceProvider>
      </BalanceVisibilityProvider>
      </ThemeProvider>
      <Toaster />
    </div>
  );
}

export default App;
